import React, { CSSProperties } from 'react';
import './bottom-modal.scss';
import { SwipeableDrawer, Box } from '@mui/material';
import { TouchableOpacity } from '../../common';
import { Divider } from '../divider';
import { CommonButton } from '../button';
import { DividerColorVariant } from '../../constants';
import { ReactComponent as SearchIcon } from '@zonofi/svg-icons/search-icon.svg';
import {
  dispatch,
  getSearchKey,
  removeSearchKey,
  updateSearchKey,
  useAppSelector,
} from '@zonofi/zono-money-store';

export interface BottomModalProps {
  isVisible?: boolean;
  title?: string;
  leftText?: string;
  onLeftAction?: () => void;
  children: React.ReactNode;
  dividerColor?: DividerColorVariant;
  rightText?: string;
  showDivider?: boolean;
  onRightAction?: () => void;
  bottomButton?: string;
  onBottomAction?: () => void;
  showHeader?: boolean;
  modalContainerStyles?: CSSProperties;
  modalChildrenStyles?: CSSProperties;
  footerStyle?: CSSProperties;
  showSearch?: boolean;
  searchPlaceholder?: string;
  isDisabled?: boolean;
  clearSearchOnClose?: boolean;
  leftTextStyle?: string;
  rightTextStyle?: string;
}

export const BottomModal: React.FC<BottomModalProps> = ({
  isVisible = false,
  showHeader = true,
  title = '',
  leftText = '',
  onLeftAction = () => {},
  children,
  rightText,
  showDivider = true,
  onRightAction,
  dividerColor = DividerColorVariant.DEFAULT,
  bottomButton = '',
  onBottomAction,
  modalContainerStyles = {},
  modalChildrenStyles = {},
  footerStyle = {},
  showSearch = false,
  searchPlaceholder = 'Search',
  isDisabled = false,
  clearSearchOnClose = true,
  leftTextStyle = '',
  rightTextStyle = '',
}) => {
  const searchKey = useAppSelector(getSearchKey);

  const clearSearchIfNeeded = () => {
    if (clearSearchOnClose) {
      dispatch(removeSearchKey());
    }
  };

  const handleClose = () => {
    clearSearchIfNeeded();
    onLeftAction?.();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isVisible}
      onClose={handleClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        className: 'bottom-modal__drawer-paper',
        style: modalContainerStyles,
      }}
    >
      <Box className="bottom-modal__container">
        {/* Drag handle */}
        <div className="bottom-modal__handle" onClick={handleClose} />

        {/* Header */}
        {showHeader && (
          <div className="bottom-modal__header">
            <TouchableOpacity onPress={handleClose}>
              <span
                className={`bottom-modal__header-text ${leftTextStyle} font-semibold`}
              >
                {leftText}
              </span>
            </TouchableOpacity>

            <span className="bottom-modal__header-title font-semibold">
              {title}
            </span>

            {rightText ? (
              <TouchableOpacity
                onPress={() => {
                  clearSearchIfNeeded();
                  onRightAction?.();
                }}
              >
                <span
                  className={`bottom-modal__header-text ${rightTextStyle} font-semibold`}
                >
                  {rightText}
                </span>
              </TouchableOpacity>
            ) : (
              <div style={{ width: 30 }} />
            )}
          </div>
        )}

        {/* Search */}
        {showSearch && (
          <div className="bottom-modal__search">
            <SearchIcon />
            <div className="bottom-modal__search-divider" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              className="bottom-modal__search-input"
              onChange={(e) => dispatch(updateSearchKey(e.target.value))}
              value={searchKey}
            />
          </div>
        )}

        {/* {showDivider && <Divider colorVariant={dividerColor} />} */}

        {/* Content */}
        <div className="bottom-modal__content" style={modalChildrenStyles}>
          {children}
        </div>

        {/* Footer */}
        {bottomButton && (
          <div className="bottom-modal__footer" style={footerStyle}>
            <CommonButton
              text={bottomButton}
              onPress={() => onBottomAction?.()}
              disabled={isDisabled}
            />
          </div>
        )}
      </Box>
    </SwipeableDrawer>
  );
};
